import { useCallback } from 'react';
import { useInfiniteQuery } from 'react-query';
import type { FacetFilter, FacetFilterValue, GoogleSearchParams } from '@jbhi-fi/apis/src/googleSearch';
import { getSearchClient } from './client';

export type UseGoogleSearchParams = {
  query?: string;
  filter?: string;
  requestedFacets?: Array<string>;
  hitsPerPage?: number;
  facets?: Array<FacetFilter>;
  sortBy?: string;
  order?: 'asc' | 'desc';
  isSaleItems?: boolean;
  isClearanceItems?: boolean;
  isExcludeMarketplace?: boolean;
  isEnabled?: boolean;
  featuredProductIds?: Array<string>;
  category?: string;
};

const buildFilter = (
  baseFilter?: string,
  isSaleItems?: boolean,
  isClearanceItems?: boolean,
  isExcludeMarketplace?: boolean
): string | undefined => {
  const parts: Array<string> = [];

  if (baseFilter) parts.push(baseFilter);
  if (isSaleItems) parts.push('attributes.onPromotion: ANY("true", "True", "1")');
  if (isClearanceItems) parts.push('attributes.bannerTags: ANY("Clearance")');
  if (isExcludeMarketplace) parts.push('attributes.isMarketplace: ANY("false")');

  return parts.length ? parts.join(' AND ') : undefined;
};

const buildFacetFilters = (facets: Array<FacetFilter>): Record<string, FacetFilterValue> | undefined => {
  if (!facets.length) return undefined;

  const result: Record<string, FacetFilterValue> = {};

  for (const { name: key, value } of facets) {
    if (Array.isArray(value)) {
      const [min, max] = value;
      result[key] = {
        ...(min > 0 ? { min } : {}),
        ...(Number.isFinite(max) && max > 0 ? { max } : {}),
      };
    } else {
      if (!result[key]) result[key] = { textValues: [] };
      result[key].textValues!.push(String(value));
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

const SORT_FIELD_MAP: Record<string, string> = {
  price: 'price',
  published_at: 'attributes.releaseDate',
  title: 'title',
  rating: 'rating',
};

const buildOrderBy = (sortBy?: string, order?: 'asc' | 'desc'): string | undefined => {
  if (!sortBy) return undefined;
  const field = SORT_FIELD_MAP[sortBy] ?? sortBy;
  return order === 'desc' ? `${field} desc` : field;
};

const toSearchParams = (params: UseGoogleSearchParams, pageToken?: string): GoogleSearchParams => ({
  ...(params.query ? { query: params.query } : {}),
  pageSize: params.hitsPerPage ?? 36,
  ...(pageToken ? { pageToken } : {}),
  filter: buildFilter(params.filter, params.isSaleItems, params.isClearanceItems, params.isExcludeMarketplace),
  facetFilters: buildFacetFilters(params.facets ?? []),
  ...(params.requestedFacets?.length ? { facets: params.requestedFacets } : {}),
  orderBy: buildOrderBy(params.sortBy, params.order),
  ...(params.featuredProductIds?.length ? { featuredProductIds: params.featuredProductIds } : {}),
  ...(params.category ? { category: params.category } : {}),
  visitorId: window?.ga?.getAll()?.[0]?.get('clientId') ?? undefined,
});

export const useGoogleCursorSearch = (params: UseGoogleSearchParams) => {
  const queryKey = [
    'googleCursorSearch',
    params.query,
    params.filter,
    params.hitsPerPage ?? 36,
    JSON.stringify(params.facets ?? []),
    JSON.stringify(params.requestedFacets ?? []),
    params.sortBy,
    params.order,
    params.isSaleItems,
    params.isClearanceItems,
    params.isExcludeMarketplace,
    JSON.stringify(params.featuredProductIds ?? []),
    params.category,
  ];

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery(
    queryKey,
    ({ pageParam }) => getSearchClient().search(toSearchParams(params, pageParam)),
    {
      enabled:
        (params.isEnabled ?? true) &&
        !!(params.query || params.filter || params.featuredProductIds?.length || params.category),
      getNextPageParam: lastPage => lastPage.nextPageToken,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );

  const allPages = data?.pages ?? [];
  const page1 = allPages[0];

  const showMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  return {
    hits: allPages.flatMap(p => p.hits),
    featuredProducts: page1?.featuredProducts ?? [],
    nbHits: page1?.totalSize ?? 0,
    facets: page1?.facets ?? {},
    facetsStats: page1?.facetsStats ?? {},
    attributionToken: page1?.attributionToken,
    hasNextPage: hasNextPage ?? false,
    isLoading,
    isFetching,
    showMore,
    loadedPageCount: allPages.length,
    usedFilter: page1?.information?.filterUsedForSearch,
  };
};
